import React from 'react';
import { graphql } from 'gatsby';
import Accordion from './Accordion';
import CompanyReviews from './CompanyReviews';
import ContactForm from './ContactForm';
import ContainedImage from './ContainedImage';
import Content from './Content';
import ExternalVideo from './ExternalVideo';
import FeaturedLogos from './FeaturedLogos';
import FeaturedProjects from './FeaturedProjects.js';
import FullWidthImage from './FullWidthImage';
import HighlightedText from './HighlightedText';
import IconCtas from './IconCtas';
import ImageContent from './ImageContent';
import ImageGallery from './ImageGallery';
import ImageLinkCards from './ImageLinkCards';
import InternalVideo from './InternalVideo';
import Tabs from './Tabs';
import TeamProfiles from './TeamProfiles';
import Testimonials from './Testimonials';

const ModularBlocks = ({ items, isBlogSingle }) =>
  items.map(item => (
    <React.Fragment key={item.id}>
      {item.model.apiKey === 'accordion_modular_block' && (
        <Accordion
          heading={item.instance.heading}
          items={item.instance.accordionItems}
          addFaqsStructuredData={item.instance.addFaqsStructuredData}
        />
      )}
      {item.model.apiKey === 'company_reviews_modular_block' && (
        <CompanyReviews heading={item.heading} />
      )}
      {item.model.apiKey === 'contact_form_modular_block' && (
        <ContactForm heading={item.heading} text={item.text} />
      )}
      {item.model.apiKey === 'contained_image_modular_block' && (
        <ContainedImage image={item.image} isBlogSingle={isBlogSingle} />
      )}
      {item.model.apiKey === 'content_modular_block' && (
        <Content
          html={item.content}
          link={item.link}
          isBlogSingle={isBlogSingle}
        />
      )}
      {item.model.apiKey === 'external_video_modular_block' && (
        <ExternalVideo video={item.externalVideo} isBlogSingle={isBlogSingle} />
      )}
      {item.model.apiKey === 'featured_logos_modular_block' && (
        <FeaturedLogos {...item.instance} />
      )}
      {item.model.apiKey === 'featured_projects_modular_block' && (
        <FeaturedProjects
          heading={item.heading}
          text={item.text}
          items={item.projects}
        />
      )}
      {item.model.apiKey === 'full_width_image_modular_block' && (
        <FullWidthImage image={item.image} isBlogSingle={isBlogSingle} />
      )}
      {item.model.apiKey === 'highlighted_text_modular_block' && (
        <HighlightedText heading={item.heading} text={item.text} />
      )}
      {item.model.apiKey === 'icon_ctas_modular_block' && (
        <IconCtas
          heading={item.heading}
          items={item.iconCtas}
          link={item.link}
        />
      )}
      {item.model.apiKey === 'image_content_modular_block' && (
        <ImageContent
          image={item.image}
          heading={item.heading}
          text={item.text}
          link={item.link}
          flip={item.flip}
        />
      )}
      {item.model.apiKey === 'image_gallery_modular_block' && (
        <ImageGallery items={item.images} />
      )}
      {item.model.apiKey === 'image_link_cards_modular_block' && (
        <ImageLinkCards heading={item.heading} items={item.imageLinkCards} />
      )}
      {item.model.apiKey === 'internal_video_modular_block' && (
        <InternalVideo video={item.internalVideo} isBlogSingle={isBlogSingle} />
      )}
      {item.model.apiKey === 'tabs_modular_block' && (
        <Tabs heading={item.instance.heading} items={item.instance.tabs} />
      )}
      {item.model.apiKey === 'team_profiles_modular_block' && (
        <TeamProfiles
          heading={item.instance.heading}
          items={item.instance.teamProfiles}
        />
      )}
      {item.model.apiKey === 'testimonials_modular_block' && (
        <Testimonials heading={item.heading} items={item.testimonials} />
      )}
    </React.Fragment>
  ));

export default ModularBlocks;

export const ModularBlockFragments = graphql`
  fragment AccordionModularBlockFragment on DatoCmsAccordionModularBlock {
    id
    model {
      apiKey
    }
    instance {
      heading
      accordionItems {
        id
        heading
        content
      }
      addFaqsStructuredData
    }
  }

  fragment CompanyReviewsModularBlockFragment on DatoCmsCompanyReviewsModularBlock {
    id
    model {
      apiKey
    }
    heading
  }

  fragment ContactFormModularBlockFragment on DatoCmsContactFormModularBlock {
    id
    model {
      apiKey
    }
    heading
    text
  }

  fragment ContainedImageModularBlockFragment on DatoCmsContainedImageModularBlock {
    id
    model {
      apiKey
    }
    image {
      gatsbyImageData(width: 1220)
      alt
    }
  }

  fragment ContentModularBlockFragment on DatoCmsContentModularBlock {
    id
    model {
      apiKey
    }
    content
    link {
      text
      page {
        ...LinkFragment
      }
    }
  }

  fragment ExternalVideoModularBlockFragment on DatoCmsExternalVideoModularBlock {
    id
    model {
      apiKey
    }
    externalVideo {
      height
      width
      provider
      providerUid
      url
    }
  }

  fragment FeaturedLogosModularBlockFragment on DatoCmsFeaturedLogosModularBlock {
    id
    model {
      apiKey
    }
    instance {
      heading
      items: logos {
        id
        logo {
          url
          alt
        }
        url
      }
    }
  }

  fragment FeaturedProjectsModularBlockFragment on DatoCmsFeaturedProjectsModularBlock {
    id
    model {
      apiKey
    }
    heading
    text
    projects {
      ...ProjectCardFragment
    }
  }

  fragment FullWidthImageModularBlockFragment on DatoCmsFullWidthImageModularBlock {
    id
    model {
      apiKey
    }
    image {
      gatsbyImageData(width: 2000, layout: FULL_WIDTH)
      alt
    }
  }

  fragment HighlightedTextModularBlockFragment on DatoCmsHighlightedTextModularBlock {
    id
    model {
      apiKey
    }
    heading
    text
  }

  fragment IconCtasModularBlockFragment on DatoCmsIconCtasModularBlock {
    id
    model {
      apiKey
    }
    heading
    iconCtas {
      id
      icon {
        url
        alt
      }
      heading
      text
    }
    link {
      text
      page {
        ...LinkFragment
      }
    }
  }

  fragment ImageContentModularBlockFragment on DatoCmsImageContentModularBlock {
    id
    model {
      apiKey
    }
    image {
      gatsbyImageData(width: 700, height: 600)
      alt
    }
    heading
    text
    link {
      text
      page {
        ...LinkFragment
      }
    }
    flip
  }

  fragment ImageGalleryModularBlockFragment on DatoCmsImageGalleryModularBlock {
    id
    model {
      apiKey
    }
    images {
      gatsbyImageData(width: 1220, height: 800)
      alt
    }
  }

  fragment ImageLinkCardsModularBlockFragment on DatoCmsImageLinkCardsModularBlock {
    id
    model {
      apiKey
    }
    heading
    imageLinkCards {
      image {
        gatsbyImageData(width: 580, height: 580)
        alt
      }
      link {
        text
        page {
          ...LinkFragment
        }
      }
      text
    }
  }

  fragment InternalVideoModularBlockFragment on DatoCmsInternalVideoModularBlock {
    id
    model {
      apiKey
    }
    internalVideo {
      format
      url
      video {
        thumbnailUrl
        mp4Url
      }
    }
  }

  fragment TabsModularBlockFragment on DatoCmsTabsModularBlock {
    id
    model {
      apiKey
    }
    instance {
      heading
      tabs {
        id
        heading
        content
      }
    }
  }

  fragment TeamProfilesModularBlockFragment on DatoCmsTeamProfilesModularBlock {
    id
    model {
      apiKey
    }
    instance {
      heading
      teamProfiles {
        id
        image {
          gatsbyImageData(width: 460, height: 460)
          alt
        }
        name
        bio
      }
    }
  }

  fragment TestimonialsModularBlockFragment on DatoCmsTestimonialsModularBlock {
    id
    model {
      apiKey
    }
    heading
    testimonials {
      id
      quote
      author
    }
  }
`;
